import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, SupplierPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ApStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ApShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const supplierLabel = (p, look) => {
  if (p?.supplier) return p.supplier.name || `Supplier ${p.supplier_id}`;
  return look.supplierLabel(p?.supplier_id, `Supplier ${p?.supplier_id ?? ''}`);
};

const emptyForm = () => ({
  supplier_id: '', payment_date: new Date().toISOString().slice(0, 10),
  amount_paid: '', paid_from_account_id: '',
});

// Supplier payments: an outgoing payment allocated across a supplier's
// open bills - the mirror image of receivables/PaymentsTab.js. DRAFT ->
// APPROVED -> POSTED; approve/void run as checker, create/post as maker.
const PayablePaymentsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.AP, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.AP, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.AP, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [openBills, setOpenBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [allocations, setAllocations] = useState({}); // invoice_id -> amount string
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!values.supplier_id) {
      setOpenBills([]);
      setAllocations({});
      return;
    }
    let alive = true;
    setLoadingBills(true);
    ledgerAPI.getPayableInvoices({ supplier_id: values.supplier_id, limit: 100 })
      .then((res) => {
        if (!alive) return;
        const list = unwrapList(res).filter((inv) => Number(inv.balance_due) > 0);
        setOpenBills(list);
        setAllocations({});
      })
      .catch(() => { if (alive) setOpenBills([]); })
      .finally(() => { if (alive) setLoadingBills(false); });
    return () => { alive = false; };
  }, [values.supplier_id]);

  const setAllocation = (invoiceId, amount) => setAllocations((p) => ({ ...p, [invoiceId]: amount }));
  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0);

  const submitCreate = async () => {
    if (!values.supplier_id) {
      showSnackbar('Supplier is required', 'error');
      return;
    }
    if (!(Number(values.amount_paid) > 0)) {
      showSnackbar('Amount paid must be greater than zero', 'error');
      return;
    }
    if (!values.paid_from_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    const allocationRows = Object.entries(allocations)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([invoice_id, amt]) => ({ invoice_id: Number(invoice_id), allocated_amount: Number(amt) }));
    if (allocationRows.length < 1) {
      showSnackbar('Allocate the payment to at least one open bill', 'error');
      return;
    }
    const payload = {
      supplier_id: Number(values.supplier_id),
      payment_date: values.payment_date,
      amount_paid: Number(values.amount_paid),
      paid_from_account_id: Number(values.paid_from_account_id),
      allocations: allocationRows,
    };
    try {
      setSaving(true);
      await ledgerAPI.createPayablePayment(payload);
      showSnackbar('Payment created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      setAllocations({});
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this payment?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Payment ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getPayablePayment(row.payment_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch payment'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidPayablePayment(voidDlg.item.payment_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Payment voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailAllocations = Array.isArray(detail?.allocations) ? detail.allocations : [];

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.AP}
        title="Supplier Payments"
        subtitle="AP payments made to suppliers, allocated against open bills"
        addLabel="New Payment"
        idKey="payment_id"
        searchPlaceholder="Search payment #, supplier..."
        emptyMessage="No payments found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getPayablePayments(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setAllocations({}); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'supplier_id', label: 'Supplier', picker: SupplierPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Payment date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Payment #', render: (p) => <strong>{p.payment_number || `PAY-${p.payment_id}`}</strong> },
          { label: 'Supplier', render: (p) => supplierLabel(p, look) },
          { label: 'Date', render: (p) => fmtDate(p.payment_date) },
          { label: 'Amount', render: (p) => formatAccountingAmount(p.amount_paid ?? p.amount), align: 'right' },
          { label: 'Status', render: (p) => <ApStatusChip status={p.status} /> },
        ]}
        rowActions={(p) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(p) }];
          if (p.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approvePayablePayment(p.payment_id)) });
          }
          if (p.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postPayablePayment(p.payment_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(p.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: p, void_reason: '' }) });
          }
          if (p.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete payment',
                message: `Delete DRAFT payment ${p.payment_number || `PAY-${p.payment_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deletePayablePayment(p.payment_id);
                    showSnackbar('Payment deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Supplier Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <SupplierPicker label="Supplier" value={values.supplier_id} onChange={(id) => setValues((p) => ({ ...p, supplier_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Payment Date" value={values.payment_date}
                onChange={(e) => setValues((p) => ({ ...p, payment_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="number" label="Amount Paid" value={values.amount_paid}
                onChange={(e) => setValues((p) => ({ ...p, amount_paid: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <BankAccountPicker label="Paid From" value={values.paid_from_account_id} onChange={(id) => setValues((p) => ({ ...p, paid_from_account_id: id }))} required />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom>Allocate to open bills</Typography>
          {!values.supplier_id ? (
            <Typography variant="body2" color="text.secondary">Select a supplier to load their open bills.</Typography>
          ) : loadingBills ? (
            <Typography variant="body2" color="text.secondary">Loading open bills...</Typography>
          ) : openBills.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No open bills for this supplier.</Typography>
          ) : (
            <>
              {openBills.map((inv) => (
                <Box key={inv.invoice_id} display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ flex: '1 1 220px' }}>
                    {inv.invoice_number || `BILL-${inv.invoice_id}`} - Balance {formatAccountingAmount(inv.balance_due)}
                  </Typography>
                  <TextField
                    size="small" type="number" label="Allocate"
                    value={allocations[inv.invoice_id] ?? ''}
                    onChange={(e) => setAllocation(inv.invoice_id, e.target.value)}
                    inputProps={{ min: 0, max: inv.balance_due, step: 'any' }}
                    sx={{ width: 140 }}
                  />
                </Box>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
                Allocated: {formatAccountingAmount(totalAllocated)} of {formatAccountingAmount(values.amount_paid || 0)}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Payment {detail?.payment_number || (detail?.payment_id ? `PAY-${detail.payment_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {supplierLabel(detail, look)} - {fmtDate(detail.payment_date)}
              </Typography>
              <DataTable
                columns={['Bill', 'Allocated Amount']}
                data={detailAllocations}
                loading={false}
                emptyMessage="No allocations."
                renderRow={(a, i) => (
                  <TableRow key={a.allocation_id ?? i}>
                    <TableCell>{a.invoice?.invoice_number || `BILL-${a.invoice_id}`}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(a.allocated_amount)}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Amount: <strong>{formatAccountingAmount(detail.amount_paid ?? detail.amount)}</strong></Typography>
                <ApStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void payment ${voidDlg.item?.payment_number || (voidDlg.item ? `PAY-${voidDlg.item.payment_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default PayablePaymentsTab;
